import type { EvidenceEvent } from "@knowyet/sdk/evidence";

type Watch = (taskId: string, signal?: AbortSignal) => AsyncIterable<EvidenceEvent>;
const transient = new Set([
  "NETWORK_ERROR", "TIMEOUT", "STREAM_IDLE_TIMEOUT", "INCOMPLETE_TASK_STREAM",
  "HTTP_502", "HTTP_503", "HTTP_504", "SERVICE_UNAVAILABLE",
]);
function wait(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    signal.throwIfAborted();
    const abort = () => { clearTimeout(timer); reject(signal.reason); };
    const timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, ms);
    signal.addEventListener("abort", abort, { once: true });
  });
}

/** Reconnect the same read-only subscription. Never submit or resume a workflow. */
export async function* watchTask(
  watch: Watch, taskId: string, signal: AbortSignal,
  options: { retries?: number; delayMs?: number } = {},
): AsyncGenerator<EvidenceEvent> {
  for (let attempt = 0; ; attempt++) {
    signal.throwIfAborted();
    try {
      for await (const event of watch(taskId, signal)) {
        yield event;
        if (["completed", "failed", "cancelled"].includes(event.type)) return;
      }
      // The SDK normally throws this itself; retain the terminal-event contract.
      throw Error("INCOMPLETE_TASK_STREAM");
    } catch (error) {
      if (signal.aborted) throw error;
      const code = error && typeof error === "object" && "code" in error
        ? String(error.code) : error instanceof Error ? error.message : "";
      if (!transient.has(code) || attempt >= (options.retries ?? 3)) throw error;
      yield { type: "status", status: "reconnecting" };
      await wait((options.delayMs ?? 1000) * (attempt + 1), signal);
    }
  }
}
